// Live part of the support timeline (components/illustration, TYPO3
// content block support-timeline). The cached page only carries fixed
// dates; this script derives what changes by the day: the position of
// the "today" marker in the drawing, the current support phase per
// version and the days left in it. Without the script the table still
// lists every end date, only the status column stays empty.
// Labels come from data attributes, so the texts stay translatable.
const DAY = 24 * 60 * 60 * 1000;

type Phase = 'free' | 'elts' | 'partner' | 'ended';

function parseIsoDate(value: string | undefined): Date | null {
  if (!value) {
    return null;
  }
  const [y, m, d] = value.split('-').map(Number);
  return y && m && d ? new Date(y, m - 1, d) : null;
}

function yearFraction(date: Date): number {
  const start = new Date(date.getFullYear(), 0, 1).getTime();
  const end = new Date(date.getFullYear() + 1, 0, 1).getTime();
  return date.getFullYear() + (date.getTime() - start) / (end - start);
}

function placeTodayMarker(root: HTMLElement, today: Date): void {
  const svg = root.querySelector<SVGSVGElement>('svg[data-axis-start]');
  const marker = root.querySelector<SVGGElement>('[data-th-today]');
  if (!svg || !marker) {
    return;
  }
  const start = Number(svg.dataset.axisStart);
  const end = Number(svg.dataset.axisEnd);
  const left = Number(svg.dataset.axisLeft);
  const right = Number(svg.dataset.axisRight);
  const fraction = yearFraction(today);
  if (!(end > start) || fraction < start || fraction > end) {
    return;
  }
  const x = left + ((fraction - start) / (end - start)) * (right - left);
  marker.setAttribute('transform', `translate(${x.toFixed(1)} 0)`);
  marker.removeAttribute('hidden');
}

function fillStatus(root: HTMLElement, today: Date): void {
  const labels = root.dataset;
  root.querySelectorAll<HTMLTableRowElement>('tr[data-free-until]').forEach((row) => {
    const cell = row.querySelector<HTMLElement>('[data-th-status]');
    const ends: [Phase, Date | null][] = [
      ['free', parseIsoDate(row.dataset.freeUntil)],
      ['elts', parseIsoDate(row.dataset.eltsUntil)],
      ['partner', parseIsoDate(row.dataset.partnerUntil)],
    ];
    if (!cell) {
      return;
    }
    const current = ends.find(([, end]) => end !== null && end.getTime() >= today.getTime());
    const phase: Phase = current ? current[0] : 'ended';
    row.dataset.phase = phase;

    const name = document.createElement('strong');
    name.textContent = labels[`label${phase.charAt(0).toUpperCase()}${phase.slice(1)}`] ?? phase;
    cell.replaceChildren(name);
    if (current && current[1]) {
      const days = Math.round((current[1].getTime() - today.getTime()) / DAY);
      const text = days === 1 ? labels.labelDay ?? '' : (labels.labelDays ?? '').replace('%d', days.toLocaleString('de-AT'));
      cell.append(document.createElement('br'), text);
    }
  });
}

// End dates that already lie in the past get a visible "expired" label,
// so the table reads at a glance which support phase is over.
function markPastDates(root: HTMLElement, today: Date): void {
  const label = root.dataset.labelPast ?? '';
  root.querySelectorAll<HTMLTimeElement>('time[data-th-end]').forEach((time) => {
    const end = parseIsoDate(time.dateTime);
    if (end === null || end.getTime() >= today.getTime() || time.classList.contains('th-lifecycle__past')) {
      return;
    }
    time.classList.add('th-lifecycle__past');
    const tag = document.createElement('span');
    tag.className = 'th-lifecycle__past-label';
    tag.textContent = label;
    time.after(' ', tag);
  });
}

function initSupportTimeline(): void {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  document.querySelectorAll<HTMLElement>('[data-th-lifecycle]').forEach((root) => {
    placeTodayMarker(root, today);
    fillStatus(root, today);
    markPastDates(root, today);
  });
}
document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', initSupportTimeline) : initSupportTimeline();
